import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, Clock, FolderOpen, RefreshCw, Bug, Users, Plus, AlertTriangle } from "lucide-react";
import TicketDetail from "./TicketDetail";
import api from '../../api';

export default function ValidationSteps() {
  const [validations, setValidations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [causeText, setCauseText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectionnee, setSelectionnee] = useState(null);
  const [organisation_id, setOrganisation_id] = useState(null);
  const [utilisateur_id, setUtilisateur_id] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setOrganisation_id(parseInt(user.organisation_id, 10));
        setUtilisateur_id(parseInt(user.id, 10));

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

  const ACCEPTED_VALIDATION_ID = 4;

  const sortedValidations = useMemo(() => {
    return [...validations].sort((a, b) => a.id - b.id);
  }, [validations]);

  const ticketsByValidation = useMemo(() => {
    const map = new Map();
    sortedValidations.forEach(validation => {
      map.set(validation.id, []);
    });
    
    tickets.forEach(ticket => {
      const list = map.get(ticket.validation_adminCl) || [];
      list.push(ticket);
    });
    
    map.forEach((tickets, validationId) => {
      map.set(validationId, tickets.sort((a, b) => 
        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      ));
    });
    
    return map;
  }, [tickets, sortedValidations]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [validationsRes, ticketsRes] = await Promise.all([
        api.get("/validations"),
        api.get(`/tickets/client/${organisation_id}`)
      ]);
      
      setValidations(validationsRes.data.data || validationsRes.data);
      setTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
    } finally {
      setIsLoading(false);
    }
  }, [organisation_id]);

  const fetchTicketDetail = async (ticketId) => {
    setIsLoading(true);
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      setSelectionnee(res.data.ticket);
    } catch (error) {
      console.error("Erreur chargement détail ticket:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (organisation_id) {
      fetchData();
    }
  }, [fetchData, organisation_id]);

  const onDragStart = (e, ticket) => {
    if (String(ticket.validation_adminCl) === String(ACCEPTED_VALIDATION_ID)) {
      e.preventDefault();
      return;
    }
    
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", ticket.id.toString());
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = async (e, validation) => {
    e.preventDefault();
    if (!draggedTicket) return;
    
    if (String(validation.id) === "3") {
      setPendingMove({ ticket: draggedTicket, validation });
      setShowRejectModal(true);
      setDraggedTicket(null);
      return;
    }

    if (String(draggedTicket.validation_adminCl) === String(ACCEPTED_VALIDATION_ID)) {
      setDraggedTicket(null);
      return;
    }

    if (String(draggedTicket.validation_adminCl) === String(validation.id)) {
      setDraggedTicket(null);
      return;
    }

    if (String(validation.id) === String(ACCEPTED_VALIDATION_ID)) {
      setPendingMove({ ticket: draggedTicket, validation });
      setShowConfirmModal(true);
      setDraggedTicket(null);
      return;
    }

    await performMove(draggedTicket, validation);
  };

  const performMove = async (ticket, validation, cause = null) => {
    setIsProcessing(true);
    try {
      const validationIdNumber = Number(validation.id);
      const updateData = {
        validation_adminCl: validationIdNumber,
        modifier: utilisateur_id,
        ...(String(validation.id) === String(ACCEPTED_VALIDATION_ID) && { etape_id: 1 }),
        ...(String(validation.id) === "3" && cause && { cause })
      };

      await api.post(
        `/tickets/${ticket.id}`, 
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        }
      );

      await fetchData();
    } catch (error) {
      console.error("Erreur mise à jour:", error);
      await fetchData();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmAccept = async () => {
    setIsProcessing(true);
    try {
      if (pendingMove) {
        await performMove(pendingMove.ticket, pendingMove.validation);
      }
    } finally {
      setIsProcessing(false);
      setShowConfirmModal(false);
      setPendingMove(null);
    }
  };

  const handleConfirmReject = async () => {
    setIsProcessing(true);
    try {
      if (pendingMove) {
        await performMove(pendingMove.ticket, pendingMove.validation, causeText);
      }
    } finally {
      setIsProcessing(false);
      setShowRejectModal(false);
      setCauseText("");
      setPendingMove(null);
    }
  };

  const handleCancelAccept = () => {
    setShowConfirmModal(false);
    setPendingMove(null);
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setCauseText("");
    setPendingMove(null);
  };

  const acceptedTicketsCount = useMemo(() => 
    tickets.filter(t => String(t.validation_adminCl) === String(ACCEPTED_VALIDATION_ID)).length,
    [tickets]
  );

  const pendingValidationId = useMemo(() => 
    validations.find(v => v.nom?.toLowerCase() === 'en attente')?.id || 0,
    [validations]
  );

  const pendingTicketsCount = useMemo(() => 
    tickets.filter(t => String(t.validation_adminCl) === String(pendingValidationId)).length,
    [tickets, pendingValidationId]
  );

  const getValidationIcon = (valNom) => {
    const nomLower = valNom?.toLowerCase();
    switch (nomLower) {
      case "accepté":
      case "accepted":
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case "rejeté":
      case "rejected":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "en attente":
      case "pending":
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <FolderOpen className="w-5 h-5 text-terrecuite-500" />;
    }
  };

  const getValidationColors = (valNom) => {
    const nomLower = valNom?.toLowerCase();
    switch (nomLower) {
      case "accepté":
      case "accepted":
        return {
          bg: "bg-emerald-50 border-emerald-200",
          header: "bg-emerald-100 text-emerald-800",
          card: "border-l-emerald-500 bg-white hover:bg-emerald-50",
          dropZone: "border-emerald-300 bg-emerald-50"
        };
      case "rejeté":
      case "rejected":
        return {
          bg: "bg-red-50 border-red-200",
          header: "bg-red-100 text-red-800",
          card: "border-l-red-500 bg-white hover:bg-red-50",
          dropZone: "border-red-300 bg-red-50"
        };
      case "en attente":
      case "pending":
        return {
          bg: "bg-amber-50 border-amber-200",
          header: "bg-amber-100 text-amber-800",
          card: "border-l-amber-500 bg-white hover:bg-amber-50",
          dropZone: "border-amber-300 bg-amber-50"
        };
      default:
        return {
          bg: "bg-terrecuite-50 border-terrecuite-200",
          header: "bg-terrecuite-100 text-terrecuite-800",
          card: "border-l-terrecuite-500 bg-white hover:bg-terrecuite-50",
          dropZone: "border-terrecuite-300 bg-terrecuite-50"
        };
    }
  };

  const sortTicketsByLastModified = (tickets) => {
    return [...tickets].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at);
      const dateB = new Date(b.updated_at || b.created_at);
      return dateB - dateA;
    });
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-terrecuite-50 to-white flex items-center justify-center z-50">
        <div className="flex flex-col items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-terrecuite-200 border-t-terrecuite-600 rounded-full mb-4"
          />
          <p className="text-terrecuite-600 font-medium">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(tickets) || !Array.isArray(validations)) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-terrecuite-50 to-white flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Erreur lors du chargement des données</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-terrecuite-50 via-white to-terrecuite-50 flex flex-col">
      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-[60]">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-200 max-w-sm w-full mx-4"
          >
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-6">
                <motion.div
                  className="w-16 h-16 rounded-full border-4 border-blue-100"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                  className="absolute top-0 left-0 w-16 h-16 rounded-full border-4 border-transparent border-t-blue-600"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Traitement en cours...
              </h3>
              <p className="text-sm text-gray-600">
                Veuillez patienter pendant le traitement de votre demande
              </p>
              <div className="mt-4 flex space-x-1">
                <motion.div
                  className="w-2 h-2 bg-blue-600 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                />
                <motion.div
                  className="w-2 h-2 bg-blue-600 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                />
                <motion.div
                  className="w-2 h-2 bg-blue-600 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-auto"
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-800">Confirmer l'acceptation</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir accepter le ticket "{pendingMove?.ticket?.nom}" ? 
              Cette action ne pourra pas être annulée et le ticket ne pourra plus être déplacé.
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={handleCancelAccept}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Non
              </button>
              <button
                onClick={handleConfirmAccept}
                disabled={isProcessing}
                className="px-4 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <motion.div
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Traitement...
                  </>
                ) : (
                  "Oui, accepter"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-auto"
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-800">Confirmer le rejet</h3>
            </div>
            <p className="text-gray-600 mb-3">
              Veuillez saisir une raison de rejet pour le ticket <strong>"{pendingMove?.ticket?.nom}"</strong>.
            </p>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              placeholder="Raison du rejet..."
              value={causeText}
              onChange={(e) => setCauseText(e.target.value)}
              disabled={isProcessing}
            />
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={handleCancelReject}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                disabled={!causeText.trim() || isProcessing}
                onClick={handleConfirmReject}
                className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  causeText.trim() && !isProcessing
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-300 cursor-not-allowed"
                }`}
              >
                {isProcessing ? (
                  <>
                    <motion.div
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Traitement...
                  </>
                ) : (
                  "Rejeter le ticket"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="h-16"></div>

      <div className="flex-1 flex flex-col p-4 md:p-6">
        <div className="w-full mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div 
              className="bg-white rounded-xl p-4 shadow-sm border border-terrecuite-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-terrecuite-100 rounded-lg">
                  <Users className="w-5 h-5 text-terrecuite-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xl font-bold text-terrecuite-800">{tickets.length}</p>
                  <p className="text-terrecuite-600 text-sm">Total Tickets</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-4 shadow-sm border border-terrecuite-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xl font-bold text-emerald-800">
                    {acceptedTicketsCount}
                  </p>
                  <p className="text-emerald-600 text-sm">Acceptés</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-4 shadow-sm border border-terrecuite-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xl font-bold text-amber-800">
                    {pendingTicketsCount}
                  </p>
                  <p className="text-amber-600 text-sm">En Attente</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-2 md:p-12">
          <div className="h-full">
            <div className={`h-full flex ${isMobile ? 'flex-col' : 'gap-6 overflow-x-auto'} overflow-y-hidden pb-6 px-2`}>
              <AnimatePresence>
                {validations.map((validation, idx) => {
                  const relatedTickets = tickets.filter(
                    (ticket) => String(ticket.validation_adminCl) === String(validation.id)
                  );
                  
                  const sortedTickets = sortTicketsByLastModified(relatedTickets);
                  const colors = getValidationColors(validation.nom);
                  const isAcceptedColumn = String(validation.id) === String(ACCEPTED_VALIDATION_ID);

                  return (
                    <motion.div
                      key={validation.id}
                      className={`${isMobile ? 'w-full mb-4' : 'min-w-[320px] w-[320px] flex-shrink-0'} h-full flex flex-col rounded-xl shadow-xs border-2 ${colors.bg} ${
                        draggedTicket ? 'border-dashed border-opacity-70 ' + colors.dropZone : 'border-opacity-40'
                      }`}
                      onDragOver={onDragOver}
                      onDrop={(e) => onDrop(e, validation)}
                      initial={{ opacity: 0, y: 30, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ 
                        delay: idx * 0.1,
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }}
                      whileHover={isMobile ? {} : { scale: 1.01 }}
                    >
                      <div className={`${colors.header} px-4 py-3 rounded-t-xl flex items-center justify-between border-b ${colors.bg.replace('bg-', 'border-')} border-opacity-30`}>
                        <div className="flex items-center gap-3">
                          {getValidationIcon(validation.nom)}
                          <div>
                            <h3 className="text-base font-semibold capitalize tracking-tight">
                              {validation.nom}
                              {isAcceptedColumn && <span className="ml-2 text-xs bg-emerald-200 px-2 py-1 rounded-full">Verrouillé</span>}
                            </h3>
                            <p className="text-xs text-opacity-70 mt-0.5">
                              {relatedTickets.length} ticket{relatedTickets.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <motion.div 
                          className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-60 rounded-full"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: idx * 0.1 + 0.3 }}
                        >
                          <span className="text-sm font-bold">
                            {relatedTickets.length}
                          </span>
                        </motion.div>
                      </div>

                      <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-300px)]" 
                           style={{ 
                             scrollbarWidth: 'thin',
                             scrollbarColor: '#cbd5e1 transparent'
                           }}>
                        <style jsx>{`
                          div::-webkit-scrollbar {
                            width: 6px;
                          }
                          div::-webkit-scrollbar-track {
                            background: transparent;
                          }
                          div::-webkit-scrollbar-thumb {
                            background-color: #cbd5e1;
                            border-radius: 3px;
                          }
                          div::-webkit-scrollbar-thumb:hover {
                            background-color: #94a3b8;
                          }
                        `}</style>
                        
                        <AnimatePresence mode="popLayout">
                          {sortedTickets.length === 0 ? (
                            <motion.div 
                              className="h-full flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-gray-300 rounded-lg bg-white bg-opacity-40"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <FolderOpen className="w-7 h-7 text-gray-400 mb-2 opacity-70" />
                              <p className="text-gray-500 text-sm font-medium">Aucun ticket</p>
                              <p className="text-gray-400 text-xs mt-1">
                                {isAcceptedColumn ? "Tickets acceptés définitivement" : "Glissez un ticket ici"}
                              </p>
                            </motion.div>
                          ) : (
                            sortedTickets.map((ticket, i) => (
                              <motion.div
                                key={ticket.id}
                                onClick={() => fetchTicketDetail(ticket.id)}
                                layout
                                className={`${colors.card} p-4 rounded-lg shadow-xs border border-opacity-30 ${
                                  isAcceptedColumn 
                                    ? 'cursor-not-allowed opacity-75' 
                                    : 'cursor-grab active:cursor-grabbing hover:shadow-sm'
                                } ${
                                  draggedTicket?.id === ticket.id ? 
                                    'opacity-50 rotate-1 scale-105 shadow-md z-50' : ''
                                }`}
                                draggable={!isAcceptedColumn}
                                onDragStart={(e) => onDragStart(e, ticket)}
                                onDragEnd={() => setDraggedTicket(null)}
                                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -20, scale: 0.9 }}
                                transition={{ 
                                  delay: i * 0.05,
                                  type: "spring",
                                  stiffness: 200,
                                  damping: 20
                                }}
                                whileHover={!isAcceptedColumn && !isMobile ? { 
                                  scale: 1.02,
                                  transition: { duration: 0.1 }
                                } : {}}
                                whileDrag={!isAcceptedColumn && !isMobile ? { 
                                  scale: 1.05,
                                  rotate: 2,
                                  zIndex: 1000,
                                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                                } : {}}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="font-medium text-gray-800 text-sm line-clamp-2 leading-snug">
                                    {ticket.nom}
                                  </h4>
                                  <div className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
                                    #{ticket.id.toString().padStart(3, '0')}
                                  </div>
                                </div>
                                
                                {ticket.description && (
                                  <p className="text-xs text-gray-600 line-clamp-3 mb-3 leading-relaxed">
                                    {ticket.description}
                                  </p>
                                )}
                                
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-gray-100 gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-xs text-gray-500 font-medium">
                                      Créé: {new Date(ticket.created_at).toLocaleDateString()}
                                    </span>
                                    {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                                      <span className="text-xs text-blue-600 font-medium">
                                        Modifié: {new Date(ticket.updated_at).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    {isAcceptedColumn && (
                                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                                    )}
                                    <motion.div 
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: colors.card.split('border-l-')[1]?.split(' ')[0] || '#6b7280' }}
                                      animate={{ 
                                        scale: [1, 1.2, 1],
                                        opacity: [0.7, 1, 0.7]
                                      }}
                                      transition={{ duration: 2, repeat: Infinity }}
                                    />
                                    <span className="text-xs text-gray-400">
                                      {validation.nom}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                            ))
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
<AnimatePresence>
  {selectionnee && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        padding: "20px"
      }}
    >
      <TicketDetail
        ticket={selectionnee}
        onClose={() => setSelectionnee(null)}
      />
    </motion.div>
  )}
</AnimatePresence>
    </div>
  );
}